import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import {
  Sparkles, Send, Loader2, ChevronDown, ChevronUp, Database,
  FileBarChart, Search as SearchIcon, AlertTriangle, Wrench,
} from "lucide-react";
import { apiBaseUrl, authHeaders } from "./shared";

// Read-only Fleet-Admin Assistant (Claude Sonnet). Talks to POST /fleet/assistant
// (admin/super_admin only, gated server-side). Every answer carries provenance
// (tools/SQL/reports it used + as-of) and a per-answer cost; a month-to-date vs
// budget meter sits in the header. No data is ever written from here.

const SUGGESTIONS = [
  "How many loads are in the system, and how many delivered this week?",
  "Show me the dispatcher performance leaderboard for this week.",
  "Which trucks drove the most unassigned (idle) miles this month?",
  "What's our fleet-wide profit summary month-to-date?",
  "List loads with off-route or fuel exceptions recently.",
];

function fmtUsd(n) {
  if (n == null) return "—";
  return n < 1 ? `$${n.toFixed(4)}` : `$${n.toFixed(2)}`;
}

// Minimal, injection-safe inline formatter: **bold**, `code`, and newlines.
function renderRich(text) {
  const lines = String(text || "").split("\n");
  return lines.map((line, li) => (
    <React.Fragment key={li}>
      {renderInline(line)}
      {li < lines.length - 1 && <br />}
    </React.Fragment>
  ));
}
function renderInline(line) {
  const parts = [];
  const re = /(\*\*[^*]+\*\*|`[^`]+`)/g;
  let last = 0, m, k = 0;
  while ((m = re.exec(line)) !== null) {
    if (m.index > last) parts.push(line.slice(last, m.index));
    const tok = m[0];
    if (tok.startsWith("**")) parts.push(<strong key={k++}>{tok.slice(2, -2)}</strong>);
    else parts.push(<code key={k++} style={styles.codeInline}>{tok.slice(1, -1)}</code>);
    last = m.index + tok.length;
  }
  if (last < line.length) parts.push(line.slice(last));
  return parts;
}

function toolMeta(p) {
  const t = p.tool;
  if (t === "run_sql") return { Icon: Database, label: "SQL query", detail: p.executed_sql };
  if (t === "call_report") return { Icon: FileBarChart, label: `Report: ${p.report || ""}`, detail: null };
  if (t === "search_schema") return { Icon: SearchIcon, label: "Schema search", detail: p.input && p.input.query };
  if (t === "describe_table") return { Icon: SearchIcon, label: `Describe: ${(p.input && p.input.table) || ""}`, detail: null };
  if (t === "make_report") return { Icon: FileBarChart, label: `Built report: ${p.report_title || ""}`, detail: null };
  return { Icon: Wrench, label: t || "tool", detail: null };
}

function Provenance({ items }) {
  const [open, setOpen] = useState(false);
  if (!items || !items.length) return null;
  return (
    <div style={styles.provWrap}>
      <button style={styles.provToggle} onClick={() => setOpen((o) => !o)}>
        {open ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
        How this was answered · {items.length} step{items.length === 1 ? "" : "s"}
      </button>
      {open && (
        <div style={styles.provList}>
          {items.map((p, i) => {
            const { Icon, label, detail } = toolMeta(p);
            return (
              <div key={i} style={styles.provItem}>
                <div style={styles.provHead}>
                  <Icon size={13} color="var(--brand)" />
                  <span style={styles.provLabel}>{label}</span>
                  {p.rows != null && <span style={styles.provBadge}>{p.rows} rows{p.truncated ? " (truncated)" : ""}</span>}
                  {p.source === "canonical_report" && <span style={styles.provBadgeGreen}>canonical</span>}
                  {p.error && <span style={styles.provBadgeRed}>error</span>}
                </div>
                {detail && <pre style={styles.provSql}>{detail}</pre>}
                {p.tables && p.tables.length > 0 && (
                  <div style={styles.provTables}>tables: {p.tables.join(", ")}</div>
                )}
                {p.error && <div style={styles.provErr}>{p.error}</div>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function FleetAssistant() {
  const [turns, setTurns] = useState([]); // {role, text, cost, iterations, provenance, error}
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [usage, setUsage] = useState(null);
  const [convId, setConvId] = useState(null);
  const scrollRef = useRef(null);
  const taRef = useRef(null);

  const loadUsage = async () => {
    try {
      const r = await axios.get(`${apiBaseUrl}/fleet/assistant/usage`, authHeaders());
      setUsage(r.data);
    } catch (e) { /* non-fatal */ }
  };

  useEffect(() => { loadUsage(); }, []);
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [turns, loading]);

  const send = async (qOverride) => {
    const q = (qOverride != null ? qOverride : input).trim();
    if (!q || loading) return;
    setInput("");
    setTurns((t) => [...t, { role: "user", text: q }]);
    setLoading(true);
    try {
      const r = await axios.post(
        `${apiBaseUrl}/fleet/assistant`,
        { question: q, conversation_id: convId },
        authHeaders()
      );
      const d = r.data;
      setConvId(d.conversation_id || convId);
      setTurns((t) => [...t, {
        role: "assistant", text: d.answer, cost: d.cost_usd,
        iterations: d.iterations, provenance: d.provenance,
      }]);
      if (d.budget_warning) toast("Heads up: approaching the monthly AI budget.", { icon: "⚠️" });
      loadUsage();
    } catch (e) {
      const status = e.response && e.response.status;
      const detail = (e.response && e.response.data && e.response.data.detail) || "Request failed.";
      let msg = detail;
      if (status === 402) msg = "Monthly AI budget reached. A super-admin can raise it in settings.";
      else if (status === 403) msg = "You don't have access to the assistant.";
      else if (status === 503) msg = detail || "Assistant is currently unavailable.";
      setTurns((t) => [...t, { role: "assistant", error: true, text: msg }]);
      if (status === 402 || status === 503) toast.error(msg);
      loadUsage();
    } finally {
      setLoading(false);
      if (taRef.current) taRef.current.focus();
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
  };

  const pct = usage && usage.pct_used != null ? usage.pct_used : null;
  const barColor = pct == null ? "var(--brand)" : pct >= 100 ? "#dc2626" : pct >= (usage.warn_pct || 80) ? "#d97706" : "var(--brand)";

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div style={styles.titleWrap}>
          <div style={styles.titleIcon}><Sparkles size={18} color="#fff" /></div>
          <div>
            <h1 style={styles.title}>Fleet Assistant</h1>
            <p style={styles.subtitle}>
              Ask anything about your fleet — loads, trucks, fuel, costs, performance. Read-only, with sources.
            </p>
          </div>
        </div>
        {usage && (
          <div style={styles.budgetCard} title="AI spend this month vs the configured cap">
            <div style={styles.budgetTop}>
              <span style={styles.budgetLabel}>AI budget (this month)</span>
              <span style={styles.budgetNums}>{fmtUsd(usage.month_to_date_usd)} / {fmtUsd(usage.budget_usd)}</span>
            </div>
            <div style={styles.budgetTrack}>
              <div style={{ ...styles.budgetFill, width: `${Math.min(100, pct || 0)}%`, background: barColor }} />
            </div>
            <div style={styles.budgetMeta}>{usage.model}{pct != null ? ` · ${pct}% used` : ""}</div>
          </div>
        )}
      </div>

      <div ref={scrollRef} style={styles.chat}>
        {turns.length === 0 && !loading && (
          <div style={styles.empty}>
            <div style={styles.emptyIcon}><Sparkles size={26} color="var(--brand)" /></div>
            <div style={styles.emptyTitle}>What would you like to know?</div>
            <div style={styles.emptySub}>Try one of these, or ask your own question.</div>
            <div style={styles.suggestions}>
              {SUGGESTIONS.map((s, i) => (
                <button key={i} style={styles.suggestion} onClick={() => send(s)}>{s}</button>
              ))}
            </div>
          </div>
        )}

        {turns.map((t, i) => (
          t.role === "user" ? (
            <div key={i} style={styles.userRow}>
              <div style={styles.userBubble}>{t.text}</div>
            </div>
          ) : (
            <div key={i} style={styles.aiRow}>
              <div style={styles.aiAvatar}><Sparkles size={14} color="var(--brand)" /></div>
              <div style={{ ...styles.aiBubble, ...(t.error ? styles.aiBubbleError : {}) }}>
                {t.error && <div style={styles.errHead}><AlertTriangle size={14} /> Couldn’t answer</div>}
                <div style={styles.aiText}>{renderRich(t.text)}</div>
                {!t.error && (
                  <>
                    <Provenance items={t.provenance} />
                    <div style={styles.aiFooter}>
                      <span>{fmtUsd(t.cost)}</span>
                      <span style={styles.dot}>·</span>
                      <span>{t.iterations} step{t.iterations === 1 ? "" : "s"}</span>
                    </div>
                  </>
                )}
              </div>
            </div>
          )
        ))}

        {loading && (
          <div style={styles.aiRow}>
            <div style={styles.aiAvatar}><Sparkles size={14} color="var(--brand)" /></div>
            <div style={styles.aiBubble}>
              <div style={styles.thinking}><Loader2 size={15} className="spin" /> Thinking…</div>
            </div>
          </div>
        )}
      </div>

      <div style={styles.composer}>
        <textarea
          ref={taRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Ask about loads, trucks, fuel, costs, dispatchers…"
          rows={1}
          style={styles.textarea}
          disabled={loading}
        />
        <button
          style={{ ...styles.sendBtn, ...((!input.trim() || loading) ? styles.sendBtnDisabled : {}) }}
          onClick={() => send()}
          disabled={!input.trim() || loading}
          title="Send"
        >
          {loading ? <Loader2 size={16} className="spin" /> : <Send size={16} />}
        </button>
      </div>
      <div style={styles.disclaimer}>
        Read-only · answers come with their sources · the assistant cannot change any data.
      </div>

      <style>{`@keyframes spin{to{transform:rotate(360deg)}} .spin{animation:spin .9s linear infinite}`}</style>
    </div>
  );
}

const styles = {
  page: { display: "flex", flexDirection: "column", height: "calc(100vh - 140px)", maxWidth: 940, margin: "0 auto", width: "100%" },
  header: { display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, marginBottom: 12, flexWrap: "wrap" },
  titleWrap: { display: "flex", gap: 12, alignItems: "center" },
  titleIcon: {
    width: 38, height: 38, borderRadius: 11, flexShrink: 0,
    background: "linear-gradient(135deg, var(--brand), #12459e)",
    display: "flex", alignItems: "center", justifyContent: "center",
    boxShadow: "0 4px 14px rgba(29,111,216,0.35)",
  },
  title: { fontSize: 20, fontWeight: 800, color: "var(--ink)", margin: 0, letterSpacing: "-0.01em" },
  subtitle: { fontSize: 12.5, color: "var(--muted)", margin: "2px 0 0" },

  budgetCard: { background: "var(--card)", border: "1px solid var(--line)", borderRadius: "var(--radius-md)", padding: "10px 14px", minWidth: 220 },
  budgetTop: { display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12 },
  budgetLabel: { fontSize: 11, color: "var(--muted)", fontWeight: 600 },
  budgetNums: { fontSize: 13, fontWeight: 800, color: "var(--ink)", fontVariantNumeric: "tabular-nums" },
  budgetTrack: { height: 6, background: "var(--bg)", borderRadius: 999, marginTop: 7, overflow: "hidden", border: "1px solid var(--line)" },
  budgetFill: { height: "100%", borderRadius: 999, transition: "width .3s ease" },
  budgetMeta: { fontSize: 10.5, color: "var(--muted)", marginTop: 5, textTransform: "capitalize" },

  chat: { flex: 1, overflowY: "auto", display: "flex", flexDirection: "column", gap: 16, padding: "8px 2px 16px" },

  empty: { margin: "auto", textAlign: "center", maxWidth: 560, padding: "20px 0" },
  emptyIcon: {
    width: 54, height: 54, borderRadius: 16, margin: "0 auto 14px",
    background: "color-mix(in srgb, var(--brand) 12%, transparent)",
    display: "flex", alignItems: "center", justifyContent: "center",
  },
  emptyTitle: { fontSize: 17, fontWeight: 800, color: "var(--ink)" },
  emptySub: { fontSize: 13, color: "var(--muted)", marginTop: 4 },
  suggestions: { display: "flex", flexDirection: "column", gap: 8, marginTop: 18 },
  suggestion: {
    textAlign: "left", background: "var(--card)", border: "1px solid var(--line)", borderRadius: "var(--radius-md)",
    padding: "11px 14px", fontSize: 13, color: "var(--ink)", cursor: "pointer", fontWeight: 500,
    transition: "border-color .15s ease, background .15s ease",
  },

  userRow: { display: "flex", justifyContent: "flex-end" },
  userBubble: {
    background: "linear-gradient(135deg, var(--brand), #1b5fc4)", color: "#fff", padding: "10px 14px",
    borderRadius: "14px 14px 4px 14px", maxWidth: "80%", fontSize: 13.5, lineHeight: 1.5, whiteSpace: "pre-wrap",
    boxShadow: "0 2px 8px rgba(29,111,216,0.22)",
  },
  aiRow: { display: "flex", gap: 10, alignItems: "flex-start" },
  aiAvatar: {
    width: 28, height: 28, borderRadius: 8, flexShrink: 0, marginTop: 2,
    background: "color-mix(in srgb, var(--brand) 12%, transparent)",
    display: "flex", alignItems: "center", justifyContent: "center",
  },
  aiBubble: {
    background: "var(--card)", border: "1px solid var(--line)", borderRadius: "14px 14px 14px 4px",
    padding: "12px 15px", maxWidth: "85%", boxShadow: "var(--shadow, 0 1px 3px rgba(0,0,0,0.06))",
  },
  aiBubbleError: { borderColor: "#fecaca", background: "#fef2f2" },
  errHead: { display: "flex", alignItems: "center", gap: 6, color: "#b91c1c", fontWeight: 700, fontSize: 12.5, marginBottom: 6 },
  aiText: { fontSize: 13.5, lineHeight: 1.6, color: "var(--ink)" },
  codeInline: { background: "var(--bg)", border: "1px solid var(--line)", borderRadius: 4, padding: "1px 5px", fontSize: 12, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" },
  aiFooter: { display: "flex", alignItems: "center", gap: 7, marginTop: 10, fontSize: 11, color: "var(--muted)", fontVariantNumeric: "tabular-nums" },
  dot: { opacity: 0.5 },

  provWrap: { marginTop: 10, borderTop: "1px solid var(--line)", paddingTop: 8 },
  provToggle: {
    display: "flex", alignItems: "center", gap: 5, background: "none", border: "none", cursor: "pointer",
    color: "var(--brand)", fontSize: 11.5, fontWeight: 700, padding: 0,
  },
  provList: { display: "flex", flexDirection: "column", gap: 8, marginTop: 8 },
  provItem: { background: "var(--bg)", border: "1px solid var(--line)", borderRadius: 8, padding: "8px 10px" },
  provHead: { display: "flex", alignItems: "center", gap: 7, flexWrap: "wrap" },
  provLabel: { fontSize: 12, fontWeight: 600, color: "var(--ink)" },
  provBadge: { fontSize: 10, fontWeight: 700, color: "var(--muted)", background: "var(--card)", border: "1px solid var(--line)", borderRadius: 999, padding: "1px 7px" },
  provBadgeGreen: { fontSize: 10, fontWeight: 700, color: "#15803d", background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 999, padding: "1px 7px" },
  provBadgeRed: { fontSize: 10, fontWeight: 700, color: "#b91c1c", background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 999, padding: "1px 7px" },
  provSql: { margin: "6px 0 0", fontSize: 11.5, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", color: "var(--ink)", whiteSpace: "pre-wrap", wordBreak: "break-word", background: "var(--card)", border: "1px solid var(--line)", borderRadius: 6, padding: "6px 8px" },
  provTables: { fontSize: 11, color: "var(--muted)", marginTop: 5 },
  provErr: { fontSize: 11, color: "#b91c1c", marginTop: 5, whiteSpace: "pre-wrap" },

  thinking: { display: "flex", alignItems: "center", gap: 8, color: "var(--muted)", fontSize: 13 },

  composer: { display: "flex", gap: 10, alignItems: "flex-end", background: "var(--card)", border: "1px solid var(--line)", borderRadius: "var(--radius-md)", padding: 8, marginTop: 4 },
  textarea: {
    flex: 1, border: "none", outline: "none", background: "transparent", resize: "none",
    fontSize: 13.5, lineHeight: 1.5, color: "var(--ink)", fontFamily: "inherit", padding: "6px 8px", maxHeight: 160,
  },
  sendBtn: {
    width: 38, height: 38, borderRadius: 10, flexShrink: 0, border: "none", cursor: "pointer",
    background: "linear-gradient(135deg, var(--brand), #12459e)", color: "#fff",
    display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 2px 8px rgba(29,111,216,0.3)",
  },
  sendBtnDisabled: { background: "var(--line)", color: "var(--muted)", cursor: "not-allowed", boxShadow: "none" },
  disclaimer: { fontSize: 10.5, color: "var(--muted)", textAlign: "center", marginTop: 8 },
};
